import { stagger } from "animejs";
import { useId } from "react";

import { useT } from "@/3-features/i18n/useT";
import { BELOW, MARKET, N, sampleCurve } from "@/3-features/solutions/standardsModel";
import { handles, prefersReducedMotion,useLoopInView } from "@/4-components/animations/anime";
import { GREEN, INK } from "@/5-shared/constants/palette";

import { BentoArt, bentoLoop } from "./BentoArt";

/**
 * SBTi, the `StandardsCostCurve` chart, in the tile that leads to it.
 * The three tightening bands appear, the market price of a compliant GO climbs from
 * band to band, and the DEC curve peels off it, drops to the zero line and keeps going
 * below. Same samples as the scene (`standardsModel`) and the same labels, band
 * headings, the two curve names, the zero, shortened to what stays legible at tile size;
 * the scene keeps the axis titles and the per-band matching rules.
 *
 * `preserveAspectRatio` is `meet`, not `none`: the labels are inside the SVG, and a
 * non-uniform fit would stretch their glyphs with the plot.
 */

const VW = 240;
const VH = 150;
const X0 = 16;
const X1 = 224;
const ZERO = 88;
const UNIT = 54;
const TOP = 26;
const BOT = 138;

const xAt = (i: number) => X0 + (i / (N - 1)) * (X1 - X0);
const yAt = (v: number) => ZERO - v * UNIT;
const DIV = [xAt(4), xAt(8)];
const BANDS = ["SBTi v1", "SBTi v2", "SBTi v?"];
/** the reveal wipe, in user units, a little wider than the line so the caps clear it */
const WIPE = X1 - X0 + 6;

const points = (k: number) =>
  sampleCurve(k)
    .map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)
    .join(" ");

export function StandardsMini() {
  const { t } = useT("companies-home");
  const c = (k: string) => t(`bentoSbti.${k}`);
  // useId's ":r0:" form is legal in an id but not in a url(#…) reference, strip it
  const clipId = `sm-wipe-${useId().replace(/:/g, "")}`;
  const ref = useLoopInView<HTMLDivElement>((root) => {
    const { one, all } = handles(root);
    const market = one<SVGPolylineElement>(".sm-mkt");
    const line = one<SVGPolylineElement>(".sm-dec");
    const area = one<SVGPolygonElement>(".sm-area");
    const wipe = one<SVGRectElement>(".sm-wipe");
    if (!market || !line || !area || !wipe) return;
    // as an array, not `one`: it is both tweened and written to, and an absent node must
    // leave the timeline valid rather than needing a guard at every `.add`
    const decLabel = all<SVGTextElement>(".sm-dec-l");

    const plan = [...all<SVGElement>(".sm-band"), ...all<SVGElement>(".sm-div"), ...all<SVGElement>(".sm-zero"), ...all<SVGElement>(".sm-axis")];
    const heads = all<SVGElement>(".sm-head");
    const mktLabel = all<SVGElement>(".sm-mkt-l");
    const zeroLabel = all<SVGElement>(".sm-zero-l");

    /** the DEC curve at `k`: 0 = on the market line, 1 = on zero, 2 = below it.
     *  Its end label rides the last sample, the way it does in the scene. */
    const curve = (k: number) => {
      const at = sampleCurve(k);
      const pts = at.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(" ");
      line.setAttribute("points", pts);
      area.setAttribute("points", `${X0},${ZERO} ${pts} ${X1},${ZERO}`);
      for (const l of decLabel) l.setAttribute("y", String(yAt(at[N - 1]) + 13));
    };
    curve(0);

    /** the market line draws itself left→right behind a clip rect. In user units, not
     *  a stroke dash: the line is `non-scaling-stroke`, so a dasharray would resolve in
     *  screen px against a viewBox length and cut the tail off at any tile width. */
    const revealTo = (w: number) => wipe.setAttribute("width", String(w));
    revealTo(0);

    if (prefersReducedMotion()) {
      // the punchline, held still: the market price up, the DEC curve below zero
      for (const el of [...plan, ...heads, ...mktLabel, ...decLabel, market, line, area]) el.style.opacity = "1";
      revealTo(WIPE);
      curve(2);
      return;
    }

    const k = { v: 0 };
    const paint = () => curve(k.v);
    const w = { v: 0 };
    const wipeTo = () => revealTo(w.v);

    return (
      bentoLoop()
        // 01, il piano: le tre fasce dello standard, sempre più strette
        .add(all<SVGElement>(".sm-band"), { opacity: [0, 1], duration: 400, delay: stagger(140) }, 200)
        .add([...all<SVGElement>(".sm-div"), ...all<SVGElement>(".sm-zero"), ...all<SVGElement>(".sm-axis")], { opacity: [0, 1], duration: 400 }, 600)
        .add(heads, { opacity: [0, 1], translateY: [4, 0], duration: 400, delay: stagger(110) }, 600)
        // 02, il prezzo della GO conforme, che sale fascia dopo fascia
        .add(market, { opacity: [0, 1], duration: 200 }, 900)
        .add(w, { v: WIPE, duration: 900, onUpdate: wipeTo }, 900)
        .add(mktLabel, { opacity: [0, 1], duration: 300 }, 1600)
        // 03, la curva DEC si stacca e scende sulla linea dello zero, 04, e sotto
        .add(line, { opacity: [0, 1], duration: 300 }, 1900)
        .add(k, { v: 1, duration: 900, onUpdate: paint }, 1900)
        .add(zeroLabel, { opacity: [0, 1], duration: 250 }, 2500)
        .add(zeroLabel, { opacity: [1, 0], duration: 200 }, 2950)
        .add(k, { v: 2, duration: 900, onUpdate: paint }, 3000)
        .add(area, { opacity: [0, 1], duration: 500 }, 3300)
        .add(decLabel, { opacity: [0, 1], duration: 300 }, 3600)
        // hold, then unwind to the start so the loop restart doesn't snap
        .add(decLabel, { opacity: [1, 0], duration: 300 }, 5700)
        .add(area, { opacity: [1, 0], duration: 400 }, 5800)
        .add(k, { v: 0, duration: 900, onUpdate: paint }, 5900)
        .add([line, ...mktLabel], { opacity: [1, 0], duration: 300 }, 6600)
        .add(w, { v: 0, duration: 700, onUpdate: wipeTo }, 6900)
        .add([...plan, ...heads], { opacity: [1, 0], duration: 500 }, 7300)
    );
  });

  return (
    <BentoArt ref={ref} className="h-full">
      <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" className="h-full w-full">
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <rect className="sm-wipe" x={X0 - 3} y={0} width={0} height={VH} />
          </clipPath>
        </defs>

        {[
          [X0, DIV[0]],
          [DIV[0], DIV[1]],
          [DIV[1], X1],
        ].map(([a, b], i) => (
          <g key={i}>
            <rect className="sm-band" x={a} y={TOP} width={b - a} height={BOT - TOP} fill={i === 1 ? INK[50] : INK[100]} opacity={0} />
            <text className="sm-head" x={(a + b) / 2} y={TOP - 8} textAnchor="middle" fontSize={9} fontWeight={700} fill={INK[600]} opacity={0}>
              {BANDS[i]}
            </text>
          </g>
        ))}
        {DIV.map((x) => (
          <line key={x} className="sm-div" x1={x} y1={TOP} x2={x} y2={BOT} stroke={INK[300]} strokeWidth={1} strokeDasharray="4 4" opacity={0} />
        ))}
        <line className="sm-zero" x1={X0} y1={ZERO} x2={X1} y2={ZERO} stroke={INK[400]} strokeWidth={1.5} opacity={0} />
        <line className="sm-axis" x1={X0} y1={TOP} x2={X0} y2={BOT} stroke={INK[400]} strokeWidth={1.5} opacity={0} />
        <text className="sm-axis" x={X1} y={BOT} textAnchor="end" fontSize={7} fontWeight={600} fill={INK[400]} opacity={0}>
          {c("time")}
        </text>
        <text
          className="sm-axis"
          x={8}
          y={(TOP + BOT) / 2}
          textAnchor="middle"
          transform={`rotate(-90 8 ${(TOP + BOT) / 2})`}
          fontSize={7}
          fontWeight={600}
          fill={INK[400]}
          opacity={0}
        >
          {c("cost")}
        </text>

        <polyline
          className="sm-mkt"
          clipPath={`url(#${clipId})`}
          points={points(0)}
          fill="none"
          stroke={INK[600]}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity={0}
        />
        <text className="sm-mkt-l" x={X1} y={yAt(MARKET[N - 1]) - 8} textAnchor="end" fontSize={8} fontWeight={700} fill={INK[600]} opacity={0}>
          {c("marketLabel")}
        </text>

        <polygon className="sm-area" points={`${X0},${ZERO} ${points(0)} ${X1},${ZERO}`} fill={GREEN} fillOpacity={0.12} opacity={0} />
        <polyline
          className="sm-dec"
          points={points(0)}
          fill="none"
          stroke={GREEN}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity={0}
        />
        <text className="sm-zero-l" x={X1} y={ZERO - 7} textAnchor="end" fontSize={8} fontWeight={700} fill="var(--color-dec-green-dark)" opacity={0}>
          {c("zeroed")}
        </text>
        <text
          className="sm-dec-l"
          x={X1}
          y={yAt(BELOW[N - 1]) + 13}
          textAnchor="end"
          fontSize={8}
          fontWeight={700}
          fill="var(--color-dec-green-dark)"
          opacity={0}
        >
          {c("withDec")}
        </text>
      </svg>
    </BentoArt>
  );
}
